import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import { claimLockTarget } from './claimPutaway'
import { notify, wmsRoute } from './notification.shared'

/**
 * Endpoint custom C12 Pemusnahan FG — `/api/v1/wms/destruction-fg/*` (WF-DSP-01, entitas `fg_destruction`):
 * - baca: `queue` (antrean pack Not Good per claim), `documents`, `document?id=`, `tasks` (DSP-02).
 * - DSP-01: `schedule` (Admin). DSP-01A: `approve` / `reschedule-request` / `divert-repack` (Supervisor), `resubmit` /
 *   `assign-move` (Admin), `mark-destroyed` (Admin atau Supervisor).
 * - DSP-02 (operator): `area-gate`, `move-scan-label` (v7.50 C-12: scan label Not Good → qty terisi jumlah label, bisa
 *   diubah, maks jumlah label; ketuk baris tetap bisa), `move-save`, `move-lock`.
 * "Alihkan Sebagian ke Repack" memecah baris `fg_ng_pack` (bukan aksi workflow) dan menerbitkan tugas simpan kecil per claim
 * (`claim_store_task` sumber DIVERT, karantina NG → pallet Repack) yang dikerjakan di layar simpan Claim — Operator.
 * Perpindahan ke ZM-01 harus penuh per baris saat dikunci (laporan 18). v7.50 M-04: Unduh Excel BA dihapus.
 */

const flow = workflowRunner('WF-DSP-01', 'fg_destruction')
const ADMIN = 'admin_wh_fg'
const SPV = 'supervisor_wh_fg'
const OPERATOR = 'operator_wh_fg'
export const SOURCE_AREA = 'ZN-01'
export const TARGET_AREA = 'ZM-01'

const iso = () => new Date().toISOString()
const num = (value: unknown) => Number(value ?? 0)
const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const hasRole = (code: string) => roleCodesOf(currentUserId()).includes(code)
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
/** Tanggal hari ini menurut WIB (YYYY-MM-DD) untuk validasi "tanggalnya sudah lewat". */
const todayWib = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10)
const toIso = (date: string, time: string) => new Date(`${date}T${time}:00+07:00`).toISOString()

const docOf = (id: unknown) => {
  const doc = findRow('fg_destruction', String(id ?? ''))
  if (!doc) fail('Dokumen pemusnahan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return doc as LocalRow
}
const packsOf = (doc: LocalRow) =>
  readTable('fg_ng_pack')
    .filter(pack => idOf(pack.destruction_id) === doc.id)
    .sort((a, b) => String(a.ng_no ?? a.id).localeCompare(String(b.ng_no ?? b.id)))
const tasksOf = (doc: LocalRow) => readTable('fg_destruction_task').filter(task => idOf(task.destruction_id) === doc.id)
const linesOf = (task: LocalRow) => readTable('fg_destruction_task_line').filter(line => idOf(line.task_id) === task.id)

/* ============================================================ penelusuran per pack */

function trace(pack: LocalRow) {
  const item = findRow('claim_item', idOf(pack.claim_item_id)) as LocalRow | undefined
  const claim = findRow('claim', idOf(pack.claim_id ?? item?.claim_id)) as LocalRow | undefined
  const label = findRow('fg_label', idOf(item?.label_id)) as LocalRow | undefined
  const sku = findRow('mt_product', idOf(label?.sku_id)) as LocalRow | undefined
  const batch = findRow('fg_labeler_batch', idOf(label?.batch_id)) as LocalRow | undefined
  const machine = findRow('mt_equipment', idOf(batch?.machine_id)) as LocalRow | undefined
  const plan = findRow('delivery_plan', idOf(label?.shipped_plan_id)) as LocalRow | undefined
  const customer = findRow('mt_companies', idOf(claim?.customer_id ?? plan?.customer_id)) as LocalRow | undefined
  const reason = findRow('mt_reject_type', idOf(pack.reason_id)) as LocalRow | undefined
  const pallet = findRow('pallet', idOf(pack.pallet_id)) as LocalRow | undefined
  return {
    id: String(pack.id),
    ng_no: String(pack.ng_no ?? ''),
    status: String(pack.ng_status),
    qty_pack: num(pack.qty_pack),
    label_no: String(label?.label_no ?? ''),
    unit_level: String(label?.level ?? ''),
    sku_code: String(sku?.code ?? ''),
    sku_name: String(sku?.name ?? ''),
    pallet_code: String(pallet?.code ?? ''),
    reason: String(reason?.name ?? ''),
    claim_id: String(claim?.id ?? ''),
    claim_no: String(claim?.clm_no ?? ''),
    customer_name: String(customer?.name ?? ''),
    queued_at: String(pack.queued_at ?? pack.date_created ?? ''),
    batch_no: String(batch?.batch_no ?? ''),
    prod_date: (batch?.prod_date as string | null) ?? null,
    shift: (batch?.shift as string | null) ?? null,
    machine: String(machine?.name ?? machine?.code ?? '') || null,
    spk_ref: (batch?.spk_ref as string | null) ?? null,
    sj_no: String(plan?.sj_no ?? item?.sj_no ?? ''),
    order_no: String(plan?.plan_no ?? ''),
    shipped_at: (plan?.shipped_at as string | null) ?? (item?.shipped_at as string | null) ?? null,
    returned_at: (claim?.date_created as string | null) ?? null,
    stored_at: String(pack.queued_at ?? '') || null,
    current_area: String(pack.current_area ?? SOURCE_AREA)
  }
}

/* ============================================================ DSP-01 — antrean & dokumen */

export function queue() {
  const rows = readTable('fg_ng_pack')
    .filter(pack => pack.ng_status === 'IN_QUARANTINE' && !pack.destruction_id)
    .map(trace)
    .sort((a, b) => a.queued_at.localeCompare(b.queued_at))
  const grouped = new Map<string, { claim_id: string; claim_no: string; customer_name: string; rows: typeof rows }>()
  rows.forEach(row => {
    const group = grouped.get(row.claim_id) ?? {
      claim_id: row.claim_id,
      claim_no: row.claim_no,
      customer_name: row.customer_name,
      rows: []
    }
    group.rows.push(row)
    grouped.set(row.claim_id, group)
  })
  const groups = [...grouped.values()].map(group => ({
    ...group,
    total_pack: group.rows.reduce((sum, row) => sum + row.qty_pack, 0)
  }))
  return { groups, total_rows: rows.length, total_pack: rows.reduce((sum, row) => sum + row.qty_pack, 0) }
}

function summary(doc: LocalRow) {
  const traces = packsOf(doc).map(trace)
  const claims = [...new Set(traces.map(row => row.claim_no))]
  const customers = [...new Set(traces.map(row => row.customer_name))]
  return {
    id: String(doc.id),
    dsp_no: String(doc.dsp_no),
    status: statusCodeOf(doc),
    status_name: String(findRow('wml_status', idOf(doc.status_id))?.name ?? ''),
    plan_datetime: String(doc.plan_datetime),
    scheduled_by: userName(doc.scheduled_by_id ?? doc.user_created),
    scheduled_at: String(doc.date_created),
    claim_count: claims.length,
    customer_count: customers.length,
    claims,
    customers,
    line_count: traces.length,
    total_pack: traces.reduce((sum, row) => sum + row.qty_pack, 0),
    approver: userName(doc.approver_id),
    approved_at: (doc.approved_at as string | null) ?? null,
    plan_note: (doc.plan_note as string | null) ?? null,
    reschedule_reason: (doc.reschedule_reason as string | null) ?? null,
    done_at: (doc.done_at as string | null) ?? null
  }
}

export function documents() {
  return readTable('fg_destruction')
    .map(summary)
    .sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at))
}

const stepLabel: Record<string, string> = {
  ACT_DSP_FG_SEND: 'Rencana diajukan ke Supervisor',
  ACT_DSP_FG_APP: 'Rencana disetujui',
  ACT_DSP_FG_REVISE: 'Dikembalikan — perlu jadwal ulang',
  ACT_DSP_FG_ASSIGN: 'Tugas pemindahan diterbitkan',
  ACT_DSP_FG_DELIVER_ZM: 'Seluruh barang tiba di Area Pemusnahan',
  ACT_DSP_FG_COMPLETE: 'Ditandai sudah dimusnahkan'
}

export function document(params: { id?: string } | undefined) {
  const doc = findRow('fg_destruction', String(params?.id ?? ''))
  if (!doc) return null
  const submissionId = idOf(doc.submission_id)
  const log = readTable('wxl_submission_log')
    .filter(row => idOf(row.submission_id) === submissionId)
    .sort((a, b) => String(a.date_created).localeCompare(String(b.date_created)))
    .map(row => ({
      at: String(row.date_created),
      by: userName(row.user_created),
      event: stepLabel[String(findRow('wml_action', idOf(row.action_id))?.name ?? '')] ?? 'Perubahan status',
      reason: (row.reason as string | null) ?? null
    }))
  const tasks = tasksOf(doc).map(task => taskView(task))
  return { ...summary(doc), lines: packsOf(doc).map(trace), tasks, log }
}

/** Jadwalkan: satu dokumen untuk pack lintas claim / customer. Terbit langsung "Menunggu Approval". */
export function schedule(body: { pack_ids?: string[]; date?: string; time?: string; note?: string }) {
  requireRole('Penjadwalan pemusnahan hanya untuk Admin WH FG.', ADMIN)
  const ids = [...new Set(body.pack_ids ?? [])]
  if (!ids.length) fail('Belum ada baris yang dicentang.')
  const date = String(body.date ?? '').trim()
  const time = String(body.time ?? '').trim()
  if (!date) fail('Tanggal pelaksanaannya belum diisi.')
  if (!time) fail('Jamnya belum diisi — Supervisor menyetujui rencana yang lengkap, bukan tanggal saja.')
  if (date < todayWib()) fail('Tanggalnya sudah lewat. Pilih hari ini atau sesudahnya.')
  const packs = ids.map(id => findRow('fg_ng_pack', id) as LocalRow | undefined)
  if (packs.some(pack => !pack || pack.ng_status !== 'IN_QUARANTINE' || pack.destruction_id))
    fail('Sebagian baris sudah dijadwalkan atau berubah — muat ulang antreannya.', 'ERR_STATE_CONFLICT', 409)
  const id = newId()
  const { statusId, submissionId } = flow.start(id)
  const total = packs.reduce((sum, pack) => sum + num(pack?.qty_pack), 0)
  const dspNo = nextDocNo('DSP', 'fg_destruction', 'dsp_no')
  const doc = insertRow('fg_destruction', {
    ...auditFieldsForCreate(),
    id,
    dsp_no: dspNo,
    plan_datetime: toIso(date, time),
    status_id: statusId,
    submission_id: submissionId,
    approver_id: null,
    done_at: null,
    plan_note: body.note?.trim() || null,
    reschedule_reason: null,
    scheduled_by_id: currentUserId(),
    total_pack: total,
    approved_at: null,
    is_destroyed: false,
    destroyed_at: null
  })
  packs.forEach(pack =>
    updateRow('fg_ng_pack', String(pack?.id), {
      destruction_id: id,
      ng_status: 'SCHEDULED',
      ...auditFieldsForUpdate()
    })
  )
  flow.run(doc, 'ACT_DSP_FG_SEND', ['ACT_DSP_FG_ADMIN_FG'])
  txLog(
    'FG Destruction',
    'DSP-01',
    'schedule',
    dspNo,
    `${packs.length} baris · ${total} pack dijadwalkan ${date} ${time}`
  )
  return { id, dsp_no: dspNo, total_pack: total, lines: packs.length }
}

/* ============================================================ DSP-01A — keputusan & penugasan */

export function approve(body: { id?: string }) {
  requireRole('Persetujuan rencana pemusnahan hanya untuk Supervisor WH FG.', SPV)
  const doc = docOf(body.id)
  flow.run(doc, 'ACT_DSP_FG_APP', ['ACT_DSP_FG_SPV_FG'], { approver_id: currentUserId(), approved_at: iso() })
  txLog('FG Destruction', 'DSP-01A', 'approve', String(doc.dsp_no), 'Rencana pemusnahan disetujui Supervisor')
  return { id: doc.id }
}

export function rescheduleRequest(body: { id?: string; reason?: string }) {
  requireRole('Permintaan jadwal ulang hanya untuk Supervisor WH FG.', SPV)
  const reason = String(body.reason ?? '').trim()
  if (reason.length < 3) fail('Sebabnya wajib diisi — kalimat inilah yang dibaca Admin saat menjadwalkan ulang.')
  const doc = docOf(body.id)
  flow.run(doc, 'ACT_DSP_FG_REVISE', ['ACT_DSP_FG_SPV_FG'], { reschedule_reason: reason }, reason)
  txLog('FG Destruction', 'DSP-01A', 'reschedule-request', String(doc.dsp_no), `Dikembalikan: ${reason}`)
  return { id: doc.id }
}

export function resubmit(body: { id?: string; date?: string; time?: string }) {
  requireRole('Pengajuan ulang hanya untuk Admin WH FG.', ADMIN)
  const doc = docOf(body.id)
  const date = String(body.date ?? '').trim()
  const time = String(body.time ?? '').trim()
  if (!date || !time) fail('Tanggal dan jamnya harus diisi keduanya.')
  if (date < todayWib()) fail('Tanggalnya sudah lewat. Pilih hari ini atau sesudahnya.')
  flow.run(doc, 'ACT_DSP_FG_SEND', ['ACT_DSP_FG_ADMIN_FG'], {
    plan_datetime: toIso(date, time),
    reschedule_reason: null
  })
  txLog('FG Destruction', 'DSP-01A', 'resubmit', String(doc.dsp_no), `Jadwal diperbarui ${date} ${time}`)
  return { id: doc.id }
}

/**
 * Tugas simpan pengalihan Repack (prototype `fg.simpan_claim`, v7.7): satu tugas per claim — lot claim tidak dicampur —
 * dari pallet karantina NG ke pallet Repack terkunci, untuk Operator WH FG pertama; dikerjakan di Claim — Operator.
 */
function createDivertStoreTasks(packs: LocalRow[], note: string, dspNo: string) {
  const byClaim = new Map<string, LocalRow[]>()
  packs.forEach(pack => {
    const claimId = idOf(pack.claim_id ?? findRow('claim_item', idOf(pack.claim_item_id))?.claim_id)
    byClaim.set(claimId, [...(byClaim.get(claimId) ?? []), pack])
  })
  const operator = readTable('mt_privileges')
    .map(row => idOf(row.user))
    .find(userId => roleCodesOf(userId).includes(OPERATOR))
  const target = claimLockTarget('REPACK')
  byClaim.forEach((rows, claimId) => {
    const qty = rows.reduce((sum, row) => sum + num(row.qty_pack), 0)
    const task = insertRow('claim_store_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      spc_no: nextDocNo('SPC', 'claim_store_task', 'spc_no'),
      claim_id: claimId || null,
      category: 'REPACK',
      source: 'DIVERT',
      target_pallet_id: target.id,
      from_pallet_id: idOf(rows[0].pallet_id) || null,
      ng_pack_ids: rows.map(row => String(row.id)),
      operator_id: operator ?? null,
      qty_packs: qty,
      deviation_reason: null,
      task_status: 'ASSIGNED',
      stored_packs: null,
      done_at: null,
      note
    })
    if (operator)
      notify({
        to: { userIds: [operator] },
        type: 'PUTAWAY_ASSIGNED',
        title: `Pindahkan ${qty} pack Repack (${dspNo})`,
        body: `Pindahkan ${qty} pack claim ${String(findRow('claim', claimId)?.clm_no ?? '')} dari karantina NG ke ${String(target.code)} — buka Claim — Operator.`,
        link: wmsRoute('customer-claim/claim-operator', { store: String(task.id) }),
        ref: { type: 'claim_store_task', id: String(task.id) }
      })
  })
}

/** Alihkan sebagian pack ke Repack: baris dipecah; pack yang dialihkan keluar dari dokumen (kembali ke rombongan Repack). */
export function divertRepack(body: { id?: string; lines?: { pack_id: string; qty: number }[]; note?: string }) {
  requireRole('Pengalihan ke Repack hanya untuk Supervisor WH FG.', SPV)
  const doc = docOf(body.id)
  if (statusCodeOf(doc) !== 'DSP_FG_PENDING_APP')
    fail('Pengalihan hanya bisa selagi dokumen Menunggu Approval.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const note = String(body.note ?? '').trim()
  if (note.length < 3) fail('Keterangannya wajib — kalimat ini yang menjelaskan kenapa barangnya batal dimusnahkan.')
  const lines = (body.lines ?? []).filter(line => num(line.qty) > 0)
  if (!lines.length) fail('Isi berapa pack yang dialihkan dari paling tidak satu baris.')
  let moved = 0
  const diverted: LocalRow[] = []
  lines.forEach(line => {
    const pack = findRow('fg_ng_pack', line.pack_id)
    if (!pack || idOf(pack.destruction_id) !== doc.id) fail('Baris tidak ada di dokumen ini.', 'ERR_NOT_FOUND', 404)
    const qty = Math.floor(num(line.qty))
    if (qty > num(pack?.qty_pack)) fail(`Melebihi isi baris ini (${num(pack?.qty_pack)} pack).`)
    moved += qty
    if (qty === num(pack?.qty_pack)) {
      updateRow('fg_ng_pack', String(pack?.id), {
        destruction_id: null,
        ng_status: 'REPACK',
        note,
        ...auditFieldsForUpdate()
      })
      diverted.push(findRow('fg_ng_pack', String(pack?.id)) as LocalRow)
    } else {
      updateRow('fg_ng_pack', String(pack?.id), { qty_pack: num(pack?.qty_pack) - qty, ...auditFieldsForUpdate() })
      const split = insertRow('fg_ng_pack', {
        ...(pack as LocalRow),
        ...auditFieldsForCreate(),
        id: newId(),
        ng_no: `${String(pack?.ng_no)}-R`,
        qty_pack: qty,
        destruction_id: null,
        ng_status: 'REPACK',
        parent_pack_id: pack?.id,
        note
      })
      diverted.push(split)
    }
  })
  createDivertStoreTasks(diverted, note, String(doc.dsp_no))
  const left = packsOf(doc)
  txLog('FG Destruction', 'DSP-01A', 'divert-repack', String(doc.dsp_no), `${moved} pack dialihkan ke Repack: ${note}`)
  if (!left.length) {
    readTable('wxl_submission')
      .filter(row => row.id === idOf(doc.submission_id))
      .forEach(row => deleteRow('wxl_submission', String(row.id)))
    deleteRow('fg_destruction', String(doc.id))
    return { id: doc.id, withdrawn: true, moved }
  }
  const total = left.reduce((sum, pack) => sum + num(pack.qty_pack), 0)
  updateRow('fg_destruction', String(doc.id), { total_pack: total, ...auditFieldsForUpdate() })
  return { id: doc.id, withdrawn: false, moved, total_pack: total }
}

export function assignMove(body: { id?: string; assignments?: { operator_id: string; pack_ids: string[] }[] }) {
  requireRole('Penerbitan tugas pemindahan hanya untuk Admin WH FG.', ADMIN)
  const doc = docOf(body.id)
  if (statusCodeOf(doc) !== 'DSP_FG_APPROVED')
    fail('Tugas pemindahan terbit setelah rencana disetujui.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const assignments = (body.assignments ?? []).filter(row => row.operator_id && row.pack_ids?.length)
  if (!assignments.length) fail('Pilih paling tidak satu petugas beserta barang yang dibawanya.')
  const operators = assignments.map(row => row.operator_id)
  if (new Set(operators).size !== operators.length) fail('Satu petugas hanya boleh muncul sekali.')
  operators.forEach(id => {
    if (!roleCodesOf(id).includes(OPERATOR)) fail('Petugas harus Operator WH FG.')
  })
  const all = packsOf(doc)
  const given = assignments.flatMap(row => row.pack_ids)
  if (new Set(given).size !== given.length) fail('Satu baris tidak boleh dibagi ke dua petugas.')
  if (all.some(pack => !given.includes(String(pack.id)))) fail('Masih ada baris yang belum dibagikan ke petugas.')
  assignments.forEach(row => {
    const taskId = newId()
    const taskNo = nextDocNo('PMD', 'fg_destruction_task', 'task_no')
    insertRow('fg_destruction_task', {
      ...auditFieldsForCreate(),
      id: taskId,
      task_no: taskNo,
      destruction_id: doc.id,
      operator_id: row.operator_id,
      task_status: 'ASSIGNED',
      area_scanned: false,
      locked_at: null
    })
    row.pack_ids.forEach(packId => {
      const pack = all.find(item => item.id === packId)
      if (!pack) fail('Baris tidak ada di dokumen ini.', 'ERR_NOT_FOUND', 404)
      insertRow('fg_destruction_task_line', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: taskId,
        pack_id: packId,
        qty_planned: num(pack?.qty_pack),
        qty_moved: 0
      })
    })
  })
  flow.run(doc, 'ACT_DSP_FG_ASSIGN', ['ACT_DSP_FG_ADMIN_FG'])
  txLog(
    'FG Destruction',
    'DSP-01A',
    'assign-move',
    String(doc.dsp_no),
    `Tugas pindah ZN-01 → ZM-01 untuk ${assignments.map(row => userName(row.operator_id)).join(', ')}`
  )
  assignments.forEach(row =>
    notify({
      to: { userIds: [row.operator_id] },
      type: 'DESTRUCTION_ASSIGNED',
      title: `Tugas pindah pemusnahan ${doc.dsp_no}`,
      body: `Pindahkan ${row.pack_ids.length} baris FG dari ZN-01 ke ZM-01 — buka Pemusnahan — Operator.`,
      link: wmsRoute('destruction-fg/pemusnahan-operator'),
      ref: { type: 'fg_destruction', id: String(doc.id) }
    })
  )
  return { id: doc.id, tasks: assignments.length }
}

export function markDestroyed(body: { id?: string }) {
  requireRole('Hanya Admin atau Supervisor WH FG yang menandai pemusnahan selesai.', ADMIN, SPV)
  const doc = docOf(body.id)
  flow.run(doc, 'ACT_DSP_FG_COMPLETE', ['ACT_DSP_FG_ADMIN_FG', 'ACT_DSP_FG_SPV_FG'])
  const now = iso()
  packsOf(doc).forEach(pack =>
    updateRow('fg_ng_pack', String(pack.id), {
      ng_status: 'DESTROYED',
      destroyed_at: now,
      current_area: TARGET_AREA,
      ...auditFieldsForUpdate()
    })
  )
  txLog('FG Destruction', 'DSP-01A', 'mark-destroyed', String(doc.dsp_no), `${num(doc.total_pack)} pack dimusnahkan`)
  return { id: doc.id }
}

/* ============================================================ DSP-02 — operator */

function taskView(task: LocalRow) {
  const doc = findRow('fg_destruction', idOf(task.destruction_id)) as LocalRow
  const lines = linesOf(task).map(line => {
    const t = trace(findRow('fg_ng_pack', idOf(line.pack_id)) as LocalRow)
    return {
      id: String(line.id),
      pack_id: t.id,
      ng_no: t.ng_no,
      label_no: t.label_no,
      sku_code: t.sku_code,
      unit_level: t.unit_level,
      reason: t.reason,
      claim_no: t.claim_no,
      pallet_code: t.pallet_code,
      qty_planned: num(line.qty_planned),
      qty_moved: num(line.qty_moved)
    }
  })
  return {
    id: String(task.id),
    task_no: String(task.task_no),
    dsp_no: String(doc.dsp_no),
    destruction_id: String(doc.id),
    plan_datetime: String(doc.plan_datetime),
    operator_id: idOf(task.operator_id),
    operator_name: userName(task.operator_id),
    status: String(task.task_status),
    area_scanned: Boolean(task.area_scanned),
    lines,
    total_pack: lines.reduce((sum, line) => sum + line.qty_planned, 0),
    moved_pack: lines.reduce((sum, line) => sum + line.qty_moved, 0)
  }
}

export function tasks() {
  const me = currentUserId()
  const mine = hasRole(OPERATOR)
  return readTable('fg_destruction_task')
    .filter(task => !mine || idOf(task.operator_id) === me)
    .map(taskView)
    .sort((a, b) => Number(a.status === 'LOCKED') - Number(b.status === 'LOCKED') || a.task_no.localeCompare(b.task_no))
}

function myTask(id: unknown) {
  requireRole('Aksi hanya untuk Operator WH FG.', OPERATOR)
  const task = findRow('fg_destruction_task', String(id ?? ''))
  if (!task) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  if (idOf(task?.operator_id) !== currentUserId())
    fail('Hanya petugas yang ditugaskan bisa mengerjakan tugas ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
  if (task?.task_status !== 'ASSIGNED') fail('Tugas ini sudah dikunci.', 'ERR_STATE_CONFLICT', 409)
  return task as LocalRow
}

export function areaGate(body: { task_id?: string; code?: string }) {
  const task = myTask(body.task_id)
  if (up(body.code) !== SOURCE_AREA) fail(`Yang discan (${String(body.code ?? '')}) bukan Area NG FG ${SOURCE_AREA}.`)
  updateRow('fg_destruction_task', String(task.id), { area_scanned: true, ...auditFieldsForUpdate() })
  return { area: SOURCE_AREA }
}

/**
 * Scan label Not Good (v7.50 C-12, keputusan Q-04): kode = nomor NG baris (`fg_ng_pack.ng_no`). Hasilnya baris yang
 * ditanyakan qty-nya (terisi jumlah label, boleh diubah, maks jumlah label) — penyimpanan tetap lewat `move-save`.
 * Label yang sudah penuh diangkat → "sudah diangkat"; label milik tugas / dokumen lain ditolak.
 */
export function moveScanLabel(body: { task_id?: string; code?: string }) {
  const task = myTask(body.task_id)
  if (!task.area_scanned) fail(`Scan Area NG FG ${SOURCE_AREA} lebih dulu.`)
  const code = up(body.code)
  if (!code) fail('Kode kosong.')
  const line = linesOf(task).find(item => up(findRow('fg_ng_pack', idOf(item.pack_id))?.ng_no) === code)
  if (!line) {
    const pack = readTable('fg_ng_pack').find(row => up(row.ng_no) === code)
    fail(
      pack
        ? `Label ${code} bukan bagian tugas ${String(task.task_no)} — barang ini milik ${pack.destruction_id ? `dokumen ${String(findRow('fg_destruction', idOf(pack.destruction_id))?.dsp_no ?? 'lain')}` : 'antrean lain'}.`
        : `Yang discan (${String(body.code ?? '')}) bukan label Not Good.`,
      'ERR_FOREIGN_LABEL',
      422
    )
  }
  const planned = num(line?.qty_planned)
  if (num(line?.qty_moved) >= planned) fail(`Label ${code} sudah diangkat (${planned} pack).`, 'ERR_ALREADY_MOVED', 409)
  return {
    line_id: String(line?.id),
    ng_no: code,
    qty_planned: planned,
    qty_moved: num(line?.qty_moved),
    qty_default: planned
  }
}

export function moveSave(body: { task_id?: string; line_id?: string; qty?: number }) {
  const task = myTask(body.task_id)
  if (!task.area_scanned) fail(`Scan Area NG FG ${SOURCE_AREA} lebih dulu.`)
  const line = linesOf(task).find(item => item.id === body.line_id)
  if (!line) fail('Baris tidak ada di tugas ini.', 'ERR_NOT_FOUND', 404)
  const qty = Math.floor(num(body.qty))
  if (!(qty > 0)) fail('Qty harus lebih dari 0.')
  if (qty > num(line?.qty_planned)) fail(`Qty melebihi sisa baris ini — sisa ${num(line?.qty_planned)}`)
  updateRow('fg_destruction_task_line', String(line?.id), { qty_moved: qty, ...auditFieldsForUpdate() })
  return { line_id: line?.id, qty_moved: qty }
}

export function moveLock(body: { task_id?: string; code?: string }) {
  const task = myTask(body.task_id)
  if (!task.area_scanned) fail(`Scan Area NG FG ${SOURCE_AREA} lebih dulu.`)
  const lines = linesOf(task)
  const open = lines.filter(line => num(line.qty_moved) !== num(line.qty_planned))
  if (open.length)
    fail(`Masih ${open.length} baris yang belum penuh diangkat — isi qty sesuai jumlah yang dijadwalkan.`)
  if (up(body.code) !== TARGET_AREA)
    fail(`Yang discan (${String(body.code ?? '')}) bukan Area Pemusnahan ${TARGET_AREA}.`)
  updateRow('fg_destruction_task', String(task.id), {
    task_status: 'LOCKED',
    locked_at: iso(),
    ...auditFieldsForUpdate()
  })
  lines.forEach(line =>
    updateRow('fg_ng_pack', idOf(line.pack_id), {
      ng_status: 'IN_DESTROY_AREA',
      current_area: TARGET_AREA,
      ...auditFieldsForUpdate()
    })
  )
  const doc = docOf(task.destruction_id)
  const everyLocked = tasksOf(doc).every(item => item.id === task.id || item.task_status === 'LOCKED')
  if (everyLocked && statusCodeOf(doc) === 'DSP_FG_ASSIGNED')
    flow.run(doc, 'ACT_DSP_FG_DELIVER_ZM', ['ACT_DSP_FG_OP_FG'])
  txLog('FG Destruction', 'DSP-02', 'move-lock', String(doc.dsp_no), `${String(task.task_no)} → ${TARGET_AREA}`)
  return { doc_in_area: everyLocked, dsp_no: doc.dsp_no }
}

/* ============================================================ registrasi */

export function registerDestructionFgHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/destruction-fg'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  get('queue', () => queue())
  get('documents', () => documents())
  get('document', params => document(params as { id?: string }))
  get('tasks', () => tasks())
  post('schedule', schedule)
  post('approve', approve)
  post('reschedule-request', rescheduleRequest)
  post('resubmit', resubmit)
  post('divert-repack', divertRepack)
  post('assign-move', assignMove)
  post('mark-destroyed', markDestroyed)
  post('area-gate', areaGate)
  post('move-scan-label', moveScanLabel)
  post('move-save', moveSave)
  post('move-lock', moveLock)
}
